import {
  Box,
  Grid,
  IconButton,
  FormControl,
  TextField,
  Button,
  Autocomplete,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Cancel } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { isNull, startsWith, uniq } from 'lodash';
import { standardString } from '../../utils/utils';
import { actions } from '../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { kinshipLabel } from '../../constants/Filters';

const useStyles = makeStyles((theme) => ({
  main: {
    backgroundColor: 'rgba(0,0,0,.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100%',
    zIndex: 15,
  },
  patientBody: {
    overflow: 'auto',
    backgroundColor: theme.palette.white.main,
    width: '100%',
    height: '100%',
    padding: 8,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'noWrap',
  },
  patientClose: {
    width: 30,
    height: 30,
  },
  body: {
    '&::-webkit-scrollbar': {
      width: '1em',
    },
    '&::-webkit-scrollbar-track': {
      '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0,0,0,.2)',
    },
  },
  patientForm: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    '&::-webkit-scrollbar': {
      width: '1em',
    },
    '&::-webkit-scrollbar-track': {
      '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0,0,0,.2)',
    },
  },
  formControl: {
    paddingBlock: 8,
  },
}));

function PatientPopup(props) {
  const { handleSubmit } = props;
  const classes = useStyles();
  const organization = useSelector((state) => state.app.organization);
  const configAPI = useSelector((state) => state.app.configAPI);
  const softwareName =
    organization && organization.healthSoftware && organization.healthSoftware.name;
  const [patient, setPatient] = useState({});
  const [type, setType] = useState(0);
  const appDispatch = useDispatch();

  useEffect(() => {
    setPatient(null);
  }, [organization.id]);

  const onSubmit = () => {
    if (
      !patient ||
      !patient.name ||
      !patient.surname ||
      !patient.bornDate ||
      !patient.os ||
      !patient.plan ||
      !patient.dni
    ) {
      return appDispatch({
        type: actions.alert,
        alert: {
          snackbarOpen: true,
          snackbarMessage: 'Todos los campos son obligatorios!',
          snackbarSeverity: 'error',
        },
      });
    }
    if (/[^0-9]+/g.test(patient.dni) || patient.dni.length < 7 || patient.dni.length > 9) {
      return appDispatch({
        type: actions.alert,
        alert: {
          snackbarOpen: true,
          snackbarMessage: 'El DNI es invalido',
          snackbarSeverity: 'error',
        },
      });
    }
    handleSubmit(patient);
  };

  const setKinship = (value) => {
    if (value === 'OTRO') setPatient({ ...patient, kinship: 0 });
    if (value === 'PAREJA') setPatient({ ...patient, kinship: 10 });
    if (value === 'HIJO') setPatient({ ...patient, kinship: 20 });
    if (value === 'AMIGO') setPatient({ ...patient, kinship: 30 });
    if (value === 'PADRE/MADRE') setPatient({ ...patient, kinship: 40 });
    if (value === 'SOBRINO/A') setPatient({ ...patient, kinship: 50 });
    if (value === 'NIETO/A') setPatient({ ...patient, kinship: 60 });
    if (value === 'ABUELO/A') setPatient({ ...patient, kinship: 70 });
    if (value === 'HERMANO/A') setPatient({ ...patient, kinship: 80 });
    if (value === 'TIO/A') setPatient({ ...patient, kinship: 90 });
  };

  const genders = ['femenino', 'masculino', 'otro'];

  return (
    <Grid container className={classes.patientBody}>
      <Box width="100%" display="flex" justifyContent="space-between">
        <IconButton
          className={classes.patientClose}
          onClick={() => {
            appDispatch({ type: actions.openPatientPopup, patientPopup: { open: false } });
          }}
          size="large"
        >
          <Cancel />
        </IconButton>
        <Box mr={2}>
          <img src="/logo512.png" width="30" alt="PrestoBots" />
        </Box>
      </Box>
      <FormControl fullWidth sx={{ px: 2 }}>
        <InputLabel sx={{ left: '32px' }}>Tipo</InputLabel>
        <Select
          value={type}
          label="Día inicio"
          onChange={(e) => {
            if (e.target.value === 0) setPatient({ ...patient, kinship: null });
            setType(e.target.value);
          }}
        >
          <MenuItem value={0}>Ficha de paciente</MenuItem>
          {softwareName !== 'macena' && <MenuItem value={1}>Grupo familiar</MenuItem>}
        </Select>
      </FormControl>
      <Box>
        <Box className={classes.patientForm} px={2}>
          <FormControl fullWidth className={classes.formControl}>
            <TextField
              label="Nombre"
              value={(patient && patient.name) || ''}
              variant="outlined"
              onChange={(event) => {
                setPatient({ ...patient, name: event.target.value });
              }}
            />
          </FormControl>
          <FormControl fullWidth className={classes.formControl}>
            <TextField
              label="Apellido"
              value={(patient && patient.surname) || ''}
              variant="outlined"
              onChange={(event) => {
                setPatient({ ...patient, surname: event.target.value });
              }}
            />
          </FormControl>
          <FormControl fullWidth className={classes.formControl}>
            <TextField
              label="DNI"
              value={(patient && patient.dni) || ''}
              variant="outlined"
              onChange={(event) => {
                setPatient({ ...patient, dni: event.target.value });
              }}
            />
          </FormControl>
          <Box my={1}>
            <Autocomplete
              size="small"
              freeSolo
              value={(patient && patient.os) || ''}
              onChange={(_, value) => {
                setPatient({ ...patient, os: value });
              }}
              options={
                uniq(
                  configAPI &&
                    configAPI.obrassociales &&
                    configAPI.obrassociales
                      .filter(
                        (e) =>
                          !patient ||
                          !patient.os ||
                          (patient.os &&
                            startsWith(standardString(e.name), standardString(patient.os))),
                      )
                      .map((e) => e.name),
                ) || []
              }
              getOptionLabel={(option) => (typeof option === 'string' ? option : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  variant="outlined"
                  label="Obra social"
                  name="typeValue"
                />
              )}
            />
          </Box>
          <Box my={1}>
            <Autocomplete
              size="small"
              freeSolo
              value={(patient && patient.plan) || ''}
              onChange={(e, value) => {
                setPatient({ ...patient, plan: value });
              }}
              options={
                patient?.os &&
                organization &&
                configAPI?.obrassociales?.find((e) => e?.name === patient.os) &&
                configAPI.obrassociales.find((e) => e.name === patient.os).types
                  ? uniq(
                      configAPI.obrassociales
                        .find((e) => e.name === patient.os)
                        ?.types.map((type) => {
                          if (typeof type === 'object' && type !== null && 'nombre' in type) {
                            return type.nombre;
                          } else if (typeof type === 'string') {
                            return type;
                          }
                          return '';
                        }),
                    )
                  : []
              }
              getOptionLabel={(option) => (typeof option === 'string' ? option : '')}
              renderInput={(params) => (
                <TextField {...params} required variant="outlined" label="Plan" name="typeValue" />
              )}
            />
          </Box>
          <FormControl fullWidth className={classes.formControl}>
            <TextField
              label="Fecha de nacimiento"
              value={(patient && patient.bornDate) || ''}
              variant="outlined"
              onChange={(event) => {
                let text = event.target.value;
                if (
                  patient &&
                  patient.bornDate &&
                  event.target.value.length > patient.bornDate.length
                ) {
                  if (text.length === 2) text += '/';
                  else if (text.length === 5) text += '/';
                }
                setPatient({ ...patient, bornDate: text });
              }}
            />
          </FormControl>
          {softwareName !== 'macena' && (
            <Box my={1}>
              <Autocomplete
                size="small"
                freeSolo
                value={(patient && patient.gender) || ''}
                onChange={(e, value) => {
                  setPatient({ ...patient, gender: value });
                }}
                options={uniq(genders) || []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    variant="outlined"
                    label="Genero"
                    name="typeValue"
                    onChange={(event) => {
                      setPatient({ ...patient, gender: event.target.value });
                    }}
                  />
                )}
              />
            </Box>
          )}
          <FormControl fullWidth className={classes.formControl}>
            <TextField
              label="Email"
              value={(patient && patient.email) || ''}
              variant="outlined"
              onChange={(event) => {
                setPatient({ ...patient, email: event.target.value });
              }}
            />
          </FormControl>

          {type === 1 && (
            <Box my={1}>
              <Autocomplete
                size="small"
                freeSolo
                value={(patient && !isNull(patient.kinship) && kinshipLabel[patient.kinship]) || ''}
                onChange={(e, value) => setKinship(value)}
                options={Object.keys(kinshipLabel).map((key) => kinshipLabel[key])}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    variant="outlined"
                    label="Relacion"
                    name="typeValue"
                    onChange={(event) => {
                      setKinship(event.target.value);
                    }}
                  />
                )}
              />
            </Box>
          )}
        </Box>
        <Box my={2} width="100%" display="flex" flexDirection="column" alignItems="center">
          <Box my={1}>
            <Button variant="contained" onClick={onSubmit} color="primary">
              Crear ficha
            </Button>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
}

const Props = {
  handleSubmit: propTypes.func,
  type: propTypes.number,
};

PatientPopup.propTypes = Props;

export default PatientPopup;
